import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../database/firebase';

// Async thunk to complete onboarding
export const completeOnboarding = createAsyncThunk(
  'onboarding/completeOnboarding',
  async ({ userId, onboardingData }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', userId);

      // Update user document with all onboarding data
      await updateDoc(userRef, {
        ...onboardingData,
        onboardingCompleted: true,
        onboardingCompletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      return { userId, ...onboardingData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to check onboarding status
export const checkOnboardingStatus = createAsyncThunk(
  'onboarding/checkStatus',
  async (userId, { rejectWithValue }) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          onboardingCompleted: data.onboardingCompleted || false,
          userData: data,
        };
      }

      return { onboardingCompleted: false, userData: null };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  currentStep: 0,
  totalSteps: 6,

  // Step 1: Personal Information
  personalInfo: {
    name: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    mobile: '',
  },

  // Step 2: Contact & Location
  contactInfo: {
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  },

  // Step 3: Health Basics
  healthBasics: {
    height: '',
    weight: '',
    bloodGroup: '',
  },

  // Step 4: Medical History
  medicalHistory: {
    medicalRecords: [],
    medicineIntake: [],
    healthRecords: '',
    injuryRecords: '',
  },

  // Step 5: Family Information
  familyInfo: {
    maritalStatus: 'single',
    numberOfChildren: 0,
    pregnancyStatus: 'not_pregnant',
    pregnancyDetails: '',
  },

  // Step 6: Goals & Preferences
  goalsAndPreferences: {
    fitnessGoal: '',
    dietaryPreferences: '',
    targetWeight: '',
    activityLevel: '',
  },

  // Status
  onboardingCompleted: false,
  isLoading: false,
  isSaving: false,
  error: null,
  success: false,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Navigate steps
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps - 1) {
        state.currentStep += 1;
      }
    },

    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },

    goToStep: (state, action) => {
      const step = action.payload;
      if (step >= 0 && step < state.totalSteps) {
        state.currentStep = step;
      }
    },

    // Update form data
    updatePersonalInfo: (state, action) => {
      state.personalInfo = { ...state.personalInfo, ...action.payload };

      // Auto-calculate age from date of birth
      if (action.payload.dateOfBirth) {
        const today = new Date();
        const birthDate = new Date(action.payload.dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        state.personalInfo.age = age;
      }
    },

    updateContactInfo: (state, action) => {
      state.contactInfo = { ...state.contactInfo, ...action.payload };
    },

    updateHealthBasics: (state, action) => {
      state.healthBasics = { ...state.healthBasics, ...action.payload };
    },

    updateMedicalHistory: (state, action) => {
      state.medicalHistory = { ...state.medicalHistory, ...action.payload };
    },

    addMedicalRecord: (state, action) => {
      state.medicalHistory.medicalRecords.push(action.payload);
    },

    removeMedicalRecord: (state, action) => {
      state.medicalHistory.medicalRecords.splice(action.payload, 1);
    },

    addMedicine: (state, action) => {
      state.medicalHistory.medicineIntake.push(action.payload);
    },

    removeMedicine: (state, action) => {
      state.medicalHistory.medicineIntake.splice(action.payload, 1);
    },

    updateFamilyInfo: (state, action) => {
      state.familyInfo = { ...state.familyInfo, ...action.payload };
    },

    updateGoalsAndPreferences: (state, action) => {
      state.goalsAndPreferences = { ...state.goalsAndPreferences, ...action.payload };
    },

    // Reset onboarding
    resetOnboarding: () => initialState,

    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // Complete Onboarding
    builder
      .addCase(completeOnboarding.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        state.isSaving = false;
        state.success = true;
        state.onboardingCompleted = true;
        state.error = null;
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
        state.success = false;
      });

    // Check Onboarding Status
    builder
      .addCase(checkOnboardingStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkOnboardingStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.onboardingCompleted = action.payload.onboardingCompleted;
        state.error = null;
      })
      .addCase(checkOnboardingStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  nextStep,
  prevStep,
  goToStep,
  updatePersonalInfo,
  updateContactInfo,
  updateHealthBasics,
  updateMedicalHistory,
  addMedicalRecord,
  removeMedicalRecord,
  addMedicine,
  removeMedicine,
  updateFamilyInfo,
  updateGoalsAndPreferences,
  resetOnboarding,
  clearError,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
